import { observable } from "mobx";
import QueryString, * as queryString from "qs";
import { routerStore } from "./WSRouter";
import { doesQueryParamBelongToPath } from "./doesQueryParamBelongToPath";

export const queryStringStore = observable({
  /** Prefer useSearchParam instead */
  get qs(): { [key: string]: string } {
    const obj = queryString.parse(routerStore.location.search.replace(/^\?/, ""));

    return Object.keys(obj).reduce((acc, key) => {
      const val = restoreQueryStringValue(key, obj[key].toString());
      acc[key] = val;
      return acc;
    }, {});
  },
});

// prefixed all search params with cvp so its easy for customers to know what is reserved
// REMEMBER: update doesQueryParamBelongToPath when adding new search params so that url is kept clean
// REMEMBER: update doesQueryParamBelongToPath when adding new search params so that url is kept clean
export enum CVPSearchParams {
  /**
   * DO NOT READ k FROM QUERY STRING. READ IT FROM window.cvpIdConcern.
   */
  k = "k",

  /**
   * production, staging, development
   *
   * Use to enforce embedScript.js to load from given environment.
   * Can be used to test new features on the customers page before deployment to production
   */
  cvpenv = "cvpenv",
  /**
   * Time sheet
   * Often referred to as id_timesheet
   */
  cvpts = "cvpts",

  /**
   * Rekruttering
   * Often referred to as id_rekruttering
   */
  cvpr = "cvpr",

  /**
   * Tilbud
   * Often referred to as id_tilbud or id_utleie
   */
  cvpt = "cvpt",

  /**
   * Admin tab
   * Which tab is selected on the admin page
   */
  cvpat = "cvpat",

  /**
   * Which step in the questionnaire is selected
   */
  cvpstep = "cvpstep",

  /**
   * First job
   * Use for pagination of the job list
   */
  cvpfj = "cvpfj",

  /**
   * Search text
   * Used for search in job list
   */
  cvpst = "cvpst",

  /**
   * Job list order
   * Used for ordering in job list
   */
  cvpjo = "cvpjo",

  /**
   * Position types
   * Used for filtering in job list
   */
  cvppt = "cvppt",

  /**
   * Categories
   * Used for filtering in job list
   */
  cvpcat = "cvpcat",

  /**
   * Counties
   * Used for filtering in job list
   */
  cvpct = "cvpct",

  /**
   * Municipalities
   * Used for filtering in job list
   */
  cvpmun = "cvpmun",

  /** sesjonsid */
  cvpssid = "cvpssid",
  /** terminalid */
  cvpttid = "cvpttid",

  /**
   * Token
   * Used for Question form
   */
  cvptoken = "cvptoken",

  /**
   * Month
   * Used in the workschedule page
   */
  cvpmonth = "cvpmonth",

  /**
   * Year
   * Used in the workschedule page
   */
  cvpyear = "cvpyear",

  /** id of a message */
  cvpmsgid = "cvpmsgid",

  /** type of message */
  cvpmst = "cvpmst",

  /**
   * window.location.pathname is held in a search param when embedded in customer page
   *
   */
  cvpage = "cvpage",

  /**
   * Language
   *
   */
  cvplocale = "cvplocale",

  /**
   * Search for text in admin translation list
   */
  cvptls = "cvptls",

  /**
   * Search for settings on admin page
   */
  cvpas = "cvpas",

  /**
   * Language selected for the admin translation list
   */
  cvptll = "cvptll",

  /**
   * Show whole timesheet
   */
  cvpswt = "cvpswt",

  /**
   * Quick Apply Parameters
   */
  cvpqap = "cvpqap",

  /**
   * Cached Login Parameters
   */
  cvpclp = "cvpclp",

  /**
   * Parameter override redirect url
   */
  cvppou = "cvppou",

  /**
   * Parameter override type
   */
  cvppot = "cvppot",

  /**
   * General consent
   */
  cvpgc = "cvpgc",

  /**
   * fcmTestRoutine
   */
  cvpftr = "cvpftr",

  /**
   * Used for inbox pagination
   */
  cvpipg = "cvpipg",

  /**
   * Used for outbox pagination
   */
  cvpopg = "cvpopg",

  /**
   * Timesheet overview page
   * Used for pagination of the timesheet overview
   */
  cvptspg = "cvptspg",

  /**
   * Search for text in timesheet overview
   */
  cvptss = "cvptss",

  /**
   * Timesheet overview view mode: "current" (default, "Aktuelle" — last 12
   * months + next 2 months via getETimesheetsOverviewForCandidate) or
   * "archive" (drill into a specific oppdrag from the candidate's full
   * assignment history).
   */
  cvptsmode = "cvptsmode",

  /**
   * Selected oppdrag id in Arkiv-mode drilldown. When set, the list shows
   * timelister for that one oppdrag with a back-link to the oppdrag list.
   */
  cvptsoid = "cvptsoid",

  /**
   * 1 when on the start page of the PWA (root path)
   */
  cvppwa = "cvppwa",

  /**
   * Create-profile suggested email
   * Used as a high-priority override of parameterSuggestion.email on /createnewprofile.
   */
  cvpse = "cvpse",

  /**
   * Cvp candidate email
   * Reserved for surfacing/selecting a specific candidate email in flows that
   * need to scope by it. Not consumed by any page yet — keep around so the
   * value survives navigation when a future feature starts setting it.
   */
  cvpce = "cvpce",

  /**
   * Cvp candidate id
   * Reserved for surfacing/selecting a specific candidate id in flows that
   * need to scope by it. Not consumed by any page yet — keep around so the
   * value survives navigation when a future feature starts setting it.
   */
  cvpcid = "cvpcid",

  /**
   * Leave request InnstillingerId
   * Used on the leave detail page to identify which application to display.
   */
  cvplrid = "cvplrid",

  /**
   * After login redirect url
   */
  cvpalru = "cvpalru"
}
// REMEMBER: update doesQueryParamBelongToPath when adding new search params so that url is kept clean
// REMEMBER: update doesQueryParamBelongToPath when adding new search params so that url is kept clean

const queryStringMinifiedValues = {
  [CVPSearchParams.cvppt]: {
    6000: "vikariat/engasjement",
    6001: "fast stilling",
    6002: "prosjekt",
    6003: "sesongbasert",
    6004: "trainee",
    6005: "selvstendig næringsdrivende",
  },
};

export function minimizeQueryStringValues(params: { [key: string]: string }) {
  return Object.keys(params).reduce((acc, key) => {
    const val = minimizeQueryStringValue(key, params[key]);
    acc[key] = val;
    return acc;
  }, {});
}

function minimizeQueryStringValue(key: string, value: string) {
  let minified = value;
  for (const [minifiedValue, originalValue] of Object.entries(
    queryStringMinifiedValues[key] || {}
  )) {
    if (minified.indexOf(originalValue as string) > -1) {
      minified = minified.replace(originalValue as string, minifiedValue);
    }
  }

  return minified;
}

function restoreQueryStringValue(key: string, value: string) {
  let restored = value;

  for (const [minifiedValue, originalValue] of Object.entries(
    queryStringMinifiedValues[key] || {}
  )) {
    if (restored.indexOf(minifiedValue as string) > -1) {
      restored = restored.replace(minifiedValue, originalValue as string);
    }
  }

  return restored;
}

export function keepOnlyParamsThatBelongToPath(
  targetPath: string,
  params: QueryString.ParsedQs
) {
  targetPath = targetPath.replace(/^\/+/g, "").trim().split("/")[0].toLowerCase();

  return Object.keys(params).reduce((acc, key) => {
    const isCVPSearchParam = Object.values(CVPSearchParams).includes(
      key as CVPSearchParams
    );

    if (!isCVPSearchParam) {
      acc[key] = params[key];
      return acc;
    }

    if (doesQueryParamBelongToPath(targetPath, key as CVPSearchParams)) {
      acc[key] = params[key];
    }

    return acc;
  }, {});
}
